import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, KeyRound, Loader2, ShieldCheck, Power, AlertCircle, X, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { adminAccountsApi, authApi, queryKeys, AdminAccount } from '@/lib/api';
import { useAuth } from '@/store/auth';

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <X className="h-5 w-5" />
            </button>
          </div>
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminAdminsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentId = user?.sub; // admin id as string

  const [showCreate, setShowCreate] = useState(false);
  const [resetting, setResetting] = useState<AdminAccount | null>(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.adminAccounts.list(),
    queryFn: () => adminAccountsApi.list(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminAccounts.all });
  const onErr = (e: any) => setError(e?.message || 'Something went wrong');

  const createMutation = useMutation({
    mutationFn: (d: { email: string; name: string; password: string }) => adminAccountsApi.create(d),
    onSuccess: () => { setError(null); setShowCreate(false); invalidate(); },
    onError: onErr,
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { isActive?: boolean; name?: string } }) => adminAccountsApi.update(id, data),
    onSuccess: () => { setError(null); invalidate(); },
    onError: onErr,
  });
  const resetMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) => adminAccountsApi.resetPassword(id, password),
    onSuccess: () => { setError(null); setResetting(null); invalidate(); },
    onError: onErr,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminAccountsApi.delete(id),
    onSuccess: () => { setError(null); invalidate(); },
    onError: onErr,
  });
  const changePwMutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => { setError(null); setShowChangePw(false); setPwMsg('Your password has been changed.'); setTimeout(() => setPwMsg(null), 6000); },
    onError: onErr,
  });

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    createMutation.mutate({
      name: (fd.get('name') as string).trim(),
      email: (fd.get('email') as string).trim(),
      password: fd.get('password') as string,
    });
  };

  const handleReset = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resetting) return;
    const fd = new FormData(e.currentTarget);
    resetMutation.mutate({ id: resetting.id, password: fd.get('password') as string });
  };

  const handleChangePw = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    changePwMutation.mutate({
      currentPassword: fd.get('currentPassword') as string,
      newPassword: fd.get('newPassword') as string,
    });
  };

  const accounts = data?.accounts ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <ShieldCheck className="h-5 w-5" /> Administrator accounts
          </h2>
          <p className="text-sm text-muted-foreground">
            These logins have full access to every facility. They are not tied to any single site.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setError(null); setShowChangePw(true); }}>
            <Lock className="mr-1 h-4 w-4" /> Change my password
          </Button>
          <Button onClick={() => { setError(null); setShowCreate(true); }}>
            <Plus className="mr-1 h-4 w-4" /> Add admin
          </Button>
        </div>
      </div>

      {pwMsg && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
          {pwMsg}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : (
        <div className="space-y-2">
          {accounts.map((a) => {
            const isSelf = currentId === String(a.id);
            const busy = updateMutation.isPending || deleteMutation.isPending;
            return (
              <div key={a.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800/40">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{a.name}</span>
                    {isSelf && <Badge variant="secondary">You</Badge>}
                    {a.isActive
                      ? <Badge variant="default">Active</Badge>
                      : <Badge variant="outline" className="text-gray-500">Disabled</Badge>}
                  </div>
                  <p className="truncate text-sm text-muted-foreground">{a.email}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setError(null); setResetting(a); }}>
                    <KeyRound className="mr-1 h-3 w-3" /> Reset password
                  </Button>
                  <Button variant="outline" size="sm" disabled={isSelf || busy}
                    title={isSelf ? 'You cannot change your own active state' : undefined}
                    onClick={() => updateMutation.mutate({ id: a.id, data: { isActive: !a.isActive } })}>
                    <Power className="mr-1 h-3 w-3" /> {a.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="outline" size="sm" disabled={isSelf || busy}
                    title={isSelf ? 'You cannot delete your own account' : undefined}
                    onClick={() => {
                      if (window.confirm(`Delete admin "${a.name}" (${a.email})? This cannot be undone.`)) {
                        deleteMutation.mutate(a.id);
                      }
                    }}>
                    <Trash2 className="h-3 w-3 text-red-600" />
                  </Button>
                </div>
              </div>
            );
          })}
          {accounts.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No admin accounts.</p>
          )}
        </div>
      )}

      {showCreate && (
        <Modal title="Add administrator" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className={labelCls}>Name</label>
              <input name="name" type="text" required className={inputCls} placeholder="Jane Smith" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input name="email" type="email" required className={inputCls} placeholder="jane@example.com" autoComplete="off" />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input name="password" type="password" required minLength={8} className={inputCls} placeholder="Min 8 characters" autoComplete="new-password" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create admin'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {showChangePw && (
        <Modal title="Change my password" onClose={() => setShowChangePw(false)}>
          <form onSubmit={handleChangePw} className="space-y-3">
            <div>
              <label className={labelCls}>Current password</label>
              <input name="currentPassword" type="password" required className={inputCls} autoComplete="current-password" />
            </div>
            <div>
              <label className={labelCls}>New password</label>
              <input name="newPassword" type="password" required minLength={8} className={inputCls} placeholder="Min 8 characters" autoComplete="new-password" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowChangePw(false)}>Cancel</Button>
              <Button type="submit" disabled={changePwMutation.isPending}>
                {changePwMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Change password'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {resetting && (
        <Modal title={`Reset password — ${resetting.name}`} onClose={() => setResetting(null)}>
          <form onSubmit={handleReset} className="space-y-3">
            <div>
              <label className={labelCls}>New password</label>
              <input name="password" type="password" required minLength={8} className={inputCls} placeholder="Min 8 characters" autoComplete="new-password" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
              <Button type="submit" disabled={resetMutation.isPending}>
                {resetMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set password'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
